import {
  IonIcon,
  IonInput,
  IonItem,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToggle,
  isPlatform,
} from "@ionic/react";
import { calendarOutline } from "ionicons/icons";
import { GetColumnsWithGroupField } from "../models/windows/GetColumnsWithGroup";
import DropdownElement from "./DropdownElement";
import ISSElement, { SearchData } from "./ISSElement";

type Props = {
  field: GetColumnsWithGroupField;
  values: { [key: string]: any };
  error: any;
  onChange: (propertyName: string, newVal: any) => void;
  onError: (error: any) => void;
  onCustomValueChange: (field: string, value: string) => void;
  onEmployeeSearch: (keyword: string) => Promise<SearchData[]>;
  onPOSearch: (keyword: string) => Promise<SearchData[]>;
};

const EMPLOYEE_ID = "EmployeeId";
const CF_PURCHASE_ORDER = "CfPurchaseOrder";

export default function POReceiptFields({
  field,
  values,
  error,
  onChange,
  onError,
  onCustomValueChange,
  onEmployeeSearch,
  onPOSearch,
}: Props) {
  const {
    ValidValues,
    PropertyName = "",
    IsRequired: required,
    IdText = "",
    NetType,
    ElementType,
  } = field;

  const isEmployee = PropertyName === EMPLOYEE_ID;
  const currentValue = values[PropertyName];

  const isCustomValueField =
    PropertyName === "StringValue" ||
    PropertyName === "NumericValue" ||
    PropertyName === "DateValue";

  const handleValidation = (val: any) => {
    if (!required) return;
    const updatedError = { ...error };
    const isEmpty = !val || (typeof val === "string" && val.length === 0);

    updatedError[PropertyName] = isEmpty;
    if (error[PropertyName] !== updatedError[PropertyName]) {
      onError(updatedError);
    }
  };

  const handleValueChange = (val: any) => {
    if (isCustomValueField) {
      onCustomValueChange(IdText, val);
    } else {
      onChange(PropertyName, val);
    }
  };

  if ((isEmployee || PropertyName === CF_PURCHASE_ORDER) && ElementType) {
    if (ElementType === "Dropdown") {
      return (
        <DropdownElement
          options={ValidValues ?? []}
          value={currentValue}
          title={isEmployee ? "Select Employee" : "Select PO"}
          filterPlaceholder={isEmployee ? "Filter Employee" : "Filter PO"}
          onSelect={(val) => onChange(PropertyName, val)}
        />
      );
    }

    if (ElementType === "ISS") {
      return (
        <ISSElement
          columnName={isEmployee ? "employee_id" : "cf_purchase_order"}
          tableName={isEmployee ? "em_employee" : "po_v_computed_columns"}
          onSearch={isEmployee ? onEmployeeSearch : onPOSearch}
          onSelect={(value) => onChange(PropertyName, value)}
          value={currentValue}
          onChange={(val) => onChange(PropertyName, val)}
        />
      );
    }
  }

  if (ValidValues && ValidValues.length > 0) {
    const selectedValue = ValidValues.find((v: any) => v.Code === currentValue);
    return (
      <IonSelect
        class="wo-editable-input"
        selectedText={selectedValue?.Description || ""}
        interface="popover"
        onIonChange={(ev: any) => {
          const val = ev.detail.value;
          handleValidation(val);
          handleValueChange(val);
        }}
        onIonCancel={() => handleValidation(currentValue)}
      >
        {!required && (
          <IonSelectOption key={`${PropertyName}-empty`} value={null}>
            {" "}
          </IonSelectOption>
        )}
        {ValidValues.map((v: any, i: number) => (
          <IonSelectOption value={v.Code} key={`${PropertyName}-option-${i}`}>
            {v.Description}
          </IonSelectOption>
        ))}
      </IonSelect>
    );
  }

  if (NetType === "Boolean") {
    return (
      <IonToggle
        slot="end"
        checked={currentValue}
        onIonChange={(ev) => onChange(PropertyName, ev.detail.checked)}
      />
    );
  }

  if (NetType === "DateTime") {
    const isMobile = isPlatform("ios") || isPlatform("android");
    return (
      <IonItem class="wo-editable-container">
        <IonInput
          class="wo-editable-input-assigned"
          required={required}
          value={currentValue}
          type="date"
          onIonInput={(ev: any) => {
            const val = ev.detail.target.value;
            handleValidation(val);
            handleValueChange(val);
          }}
        />
        {isMobile && (
          <IonIcon
            icon={calendarOutline}
            slot="end"
            style={{ pointerEvents: "none" }}
          />
        )}
      </IonItem>
    );
  }

  if (ElementType === "htmlEditor" || PropertyName === "Comments") {
    return (
      <IonTextarea
        class="wo-editable-input"
        autoGrow
        required={required}
        value={currentValue}
        onIonChange={(ev: any) => {
          const val = ev.detail.value;
          handleValidation(val);
          onChange(PropertyName, val);
        }}
      />
    );
  }

  // Default fallback input
  return (
    <IonInput
      required={required}
      class="wo-editable-input"
      value={currentValue}
      type={NetType === "DateTime" ? "date" : undefined}
      onIonInput={(ev: any) => {
        const val = ev.detail.target.value;
        handleValidation(val);
        handleValueChange(val);
      }}
    />
  );
}
